import { ReactNode, useEffect, useState } from "react";
import { Portal } from "components/atoms/Portal";
import { usePopper } from "react-popper";
import { useTheme } from "styled-components";

/**
 * Clicks that land inside one of these should NOT dismiss the panel. The shared
 * Select dropdown and lookup menu portal themselves to <body> (so a containment
 * check against the panel would treat them as "outside"), and antd's date-picker
 * calendar does the same. Tagging them keeps the panel open while the user
 * interacts with the control inside it.
 */
const KEEP_OPEN_SELECTOR = "[data-filter-keepopen], .ant-picker-dropdown";

/**
 * Low-level popover used by the table filter bar: it renders a caller-supplied
 * trigger and, while open, a portaled panel positioned beneath it. Closing
 * (outside click / Escape) is owned here; opening/toggling is owned by the
 * caller so a parent can enforce "one open at a time".
 */
export const FilterPopover = ({
  isOpen,
  onClose,
  panelWidth = 280,
  renderTrigger,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  panelWidth?: number;
  renderTrigger: (props: {
    ref: (el: HTMLElement | null) => void;
  }) => ReactNode;
  children: ReactNode;
}) => {
  const theme = useTheme();
  const [triggerEl, setTriggerEl] = useState<HTMLElement | null>(null);
  const [panelEl, setPanelEl] = useState<HTMLElement | null>(null);

  const { styles, attributes } = usePopper(triggerEl, panelEl, {
    strategy: "fixed",
    placement: "bottom-start",
    modifiers: [{ name: "offset", options: { offset: [0, 6] } }],
  });

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (triggerEl?.contains(target)) return;
      if (panelEl?.contains(target)) return;
      if (target.closest?.(KEEP_OPEN_SELECTOR)) return;
      onClose();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, triggerEl, panelEl, onClose]);

  return (
    <>
      {renderTrigger({ ref: setTriggerEl })}
      {isOpen && (
        <Portal>
          <div
            ref={setPanelEl}
            style={{
              ...styles.popper,
              width: panelWidth,
              // Panels can be wider than a phone screen (Status is 520px);
              // never let one extend past the viewport.
              maxWidth: "calc(100vw - 12px)",
            }}
            {...(attributes.popper || {})}
          >
            <div
              className="border rounded-md"
              style={{
                backgroundColor: theme.dropdownBackgroundColor,
                borderColor: theme.dropdownBorderColor,
                boxShadow: theme.shadows["2xl"],
              }}
            >
              {children}
            </div>
          </div>
        </Portal>
      )}
    </>
  );
};
